import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { ActionType, LineType } from 'klinecharts'
import type { Chart } from 'klinecharts'
import { botApi, type BacktestResult } from '../../bot/api'
import { fetchBacktestEvents } from '../../bot/adapters/eventFetcher'
import type { OrderData } from '../../bot/components/GraphCandle'
import { useSSE } from '../../contexts/SSEContext'
import { SSE_EVENTS } from '../../contexts/SSEEvents'
import { backtestOrderConnector, backtestOrderMarker } from './customOverlays'
import { asOverlayId } from './types'

/**
 * Overlays the bot's last backtest orders (entries/exits) on the workbench
 * chart as compact locked markers (btBuyMarker/btSellMarker/btExitMarker).
 *
 * Orders are fetched windowed for performance: only the visible candle range
 * (padded, quantized to UTC days so repeat windows hit the eventFetcher LRU
 * cache) is loaded, and panning extends coverage by delta fetches. Markers are
 * tracked in a private map — never in drawIdsRef — so they stay invisible to
 * autosave, AI-context diffing and "Remove all drawings".
 */

const DAY = 86400
const DEBOUNCE_MS = 250
/** klinecharts iterates every overlay per repaint — cap and prune to viewport. */
const MAX_OVERLAYS = 800

interface LatestBacktest {
  btid: string
  symbol: string
  status: number
  /** Backtest range in unix seconds; end is exclusive (end_date + 1 day). */
  startTs: number | null
  endTs: number | null
}

interface CompletedSSEMessage {
  event?: string
  botId?: string
}

/** status 0 = queued, 1 = running; anything else is a finished run. */
const isTerminalStatus = (status: number) => status !== 0 && status !== 1

function parseLatestBacktest(bt: BacktestResult | null): LatestBacktest | null {
  if (!bt || !bt.btid) return null
  const cfg = bt.configInput?.backtest_config
  const startTs =
    typeof cfg?.start_date === 'string'
      ? Math.floor(Date.parse(`${cfg.start_date}T00:00:00Z`) / 1000)
      : NaN
  // end_date is date-only; +1 day makes the final day inclusive.
  const endTs =
    typeof cfg?.end_date === 'string'
      ? Math.floor(Date.parse(`${cfg.end_date}T00:00:00Z`) / 1000) + DAY
      : NaN
  return {
    btid: bt.btid,
    symbol: typeof cfg?.symbol === 'string' ? cfg.symbol : '',
    status: bt.status,
    startTs: Number.isFinite(startTs) ? startTs : null,
    endTs: Number.isFinite(endTs) ? endTs : null,
  }
}

export interface BacktestOrdersOverlayState {
  /** A last backtest exists, is finished, and its symbol matches the chart. */
  available: boolean
  /** Why the toggle is disabled (null when available). */
  unavailableReason: string | null
}

export interface BtOrderClickPayload {
  /** Every order on the clicked marker's chart bar (the backtest chart's
   *  click tooltip groups per candle the same way), time-ordered. */
  orders: OrderData[]
  /** Clicked marker's anchor: chart-space ms timestamp + price. */
  timestamp: number
  value: number
}

export function useBacktestOrdersOverlay({
  bid,
  chart,
  symbol,
  enabled,
  onOrderClick,
  onAutoShow,
}: {
  bid: string
  chart: Chart | null
  symbol: string
  /** The user's toggle; the hook derives effective visibility from it. */
  enabled: boolean
  /** Fired when a marker is clicked; drives the order-details popover. */
  onOrderClick?: (payload: BtOrderClickPayload) => void
  /** Fired when a backtest completes during the session (SSE) and its symbol
   *  matches the chart — lets the consumer flip the toggle on automatically. */
  onAutoShow?: () => void
}): BacktestOrdersOverlayState {
  const [latest, setLatest] = useState<LatestBacktest | null>(null)
  const [latestLoaded, setLatestLoaded] = useState(false)
  const [refreshNonce, setRefreshNonce] = useState(0)

  const isTerminal = latest !== null && isTerminalStatus(latest.status)
  const available =
    latest !== null && isTerminal && symbol !== '' && latest.symbol === symbol
  const active = enabled && available && chart !== null

  const unavailableReason = available
    ? null
    : !latestLoaded || latest === null
      ? latestLoaded
        ? 'Run a backtest to see its trades here'
        : null
      : !isTerminal
        ? 'Backtest still running'
        : `Last backtest ran on ${latest.symbol} — switch symbol to view its trades`

  // Ref mirrors for the long-lived subscription callback and async resumptions.
  const chartMirrorRef = useRef<Chart | null>(null)
  useLayoutEffect(() => {
    chartMirrorRef.current = chart
  }, [chart])
  const activeRef = useRef(false)
  useLayoutEffect(() => {
    activeRef.current = active
  }, [active])
  const latestRef = useRef<LatestBacktest | null>(null)
  useLayoutEffect(() => {
    latestRef.current = latest
  }, [latest])
  const onOrderClickRef = useRef(onOrderClick)
  useLayoutEffect(() => {
    onOrderClickRef.current = onOrderClick
  }, [onOrderClick])
  // The latest-fetch effect is keyed on [bid, refreshNonce], so the symbol and
  // auto-show callback must be read fresh at resolve time.
  const symbolRef = useRef(symbol)
  useLayoutEffect(() => {
    symbolRef.current = symbol
  }, [symbol])
  const onAutoShowRef = useRef(onAutoShow)
  useLayoutEffect(() => {
    onAutoShowRef.current = onAutoShow
  }, [onAutoShow])
  // Set by the SSE completion handler, consumed by the next latest-fetch
  // resolution — mount/restore fetches never auto-show.
  const pendingAutoShowRef = useRef(false)

  // Bumped on every clear; async resumptions compare and drop stale results.
  const epochRef = useRef(0)
  // Contiguous fetched range (unix sec, day-quantized); null = nothing loaded.
  const coveredRef = useRef<{ start: number; end: number } | null>(null)
  const ordersRef = useRef<Map<string | number, OrderData>>(new Map())
  // orderId → overlay id. Deliberately NOT drawIdsRef: these are not drawings.
  const overlayIdsRef = useRef<Map<string | number, string>>(new Map())
  // orderId → entry→exit connector segment id (closed orders only). Kept out
  // of drawIdsRef too — critical, since connectors use the same 'segment'
  // name as user drawings and must never be autosaved as one.
  const connectorIdsRef = useRef<Map<string | number, string>>(new Map())
  const inFlightRef = useRef(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // ---- Latest backtest (defines btid, symbol gate, fetch clamp range) ----
  // No synchronous reset here: the Workbench remounts per bid, and on an SSE
  // refetch keeping the previous markers until the new run's data lands beats
  // a clear-then-redraw flash (the btid change swaps them via the reconcile
  // effect below).
  useEffect(() => {
    let cancelled = false
    botApi
      .getLatestBacktest(bid)
      .then((data) => {
        if (cancelled) return
        const parsed = parseLatestBacktest(data)
        setLatest(parsed)
        // An in-session completion (SSE) auto-shows the trades, but only when
        // the finished run is drawable on this chart (terminal + same symbol).
        const pending = pendingAutoShowRef.current
        pendingAutoShowRef.current = false
        if (
          pending &&
          parsed !== null &&
          isTerminalStatus(parsed.status) &&
          symbolRef.current !== '' &&
          parsed.symbol === symbolRef.current
        ) {
          onAutoShowRef.current?.()
        }
      })
      .catch(() => {
        // Leave latest null — the toggle just stays disabled.
        if (!cancelled) pendingAutoShowRef.current = false
      })
      .finally(() => {
        if (!cancelled) setLatestLoaded(true)
      })
    return () => {
      cancelled = true
    }
  }, [bid, refreshNonce])

  // A backtest finishing while the workbench is open changes what "last
  // backtest" means — refetch. The btid change then flows through the
  // reconcile effect, clearing old markers and loading the new run's orders.
  useSSE(
    useCallback(
      (msg: CompletedSSEMessage) =>
        msg.event === SSE_EVENTS.BOT_BACKTEST_COMPLETED && msg.botId === bid,
      [bid],
    ),
    useCallback(() => {
      pendingAutoShowRef.current = true
      setRefreshNonce((n) => n + 1)
    }, []),
  )

  const clearAll = useCallback(() => {
    epochRef.current++
    const c = chartMirrorRef.current
    const removeAll = (ids: Map<string | number, string>) => {
      ids.forEach((id) => {
        try {
          c?.removeOverlay({ id })
        } catch {
          // Chart already disposed (unmount teardown order) — nothing to remove.
        }
      })
      ids.clear()
    }
    removeAll(overlayIdsRef.current)
    removeAll(connectorIdsRef.current)
    ordersRef.current.clear()
    coveredRef.current = null
    inFlightRef.current = false
  }, [])

  // Marker click → hand the consumer every order on that chart bar (the
  // backtest chart's click tooltip groups per candle the same way).
  const emitOrderClick = useCallback((order: OrderData) => {
    const cb = onOrderClickRef.current
    const c = chartMirrorRef.current
    if (!cb || !c) return
    const list = c.getDataList()
    const ms = order.time * 1000
    // Last bar whose timestamp <= ms; the bar bucket ends where the next starts.
    let lo = 0
    let hi = list.length
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (list[mid].timestamp <= ms) lo = mid + 1
      else hi = mid
    }
    const barIdx = lo - 1
    const barStartMs = barIdx >= 0 ? list[barIdx].timestamp : ms
    const barEndMs = barIdx + 1 < list.length ? list[barIdx + 1].timestamp : Infinity
    const orders = [...ordersRef.current.values()]
      .filter((o) => {
        const t = o.time * 1000
        return t >= barStartMs && t < barEndMs
      })
      .sort((a, b) => a.time - b.time)
    cb({ orders: orders.length > 0 ? orders : [order], timestamp: ms, value: order.price })
  }, [])

  // Create markers for fetched orders that lie within the LOADED candle range.
  // klinecharts resolves an out-of-range timestamp to the nearest bar, which
  // would pile early markers onto the left edge — those orders stay in
  // ordersRef and materialize on a later pass once pagination loads their bars
  // (OnVisibleRangeChange also fires when new data is applied).
  const reconcileOverlays = useCallback(() => {
    const c = chartMirrorRef.current
    if (!c || !activeRef.current) return
    const list = c.getDataList()
    if (list.length === 0) return
    const firstMs = list[0].timestamp
    const lastMs = list[list.length - 1].timestamp
    ordersRef.current.forEach((order, orderId) => {
      const ms = order.time * 1000
      if (!overlayIdsRef.current.has(orderId) && ms >= firstMs && ms <= lastMs) {
        const marker = backtestOrderMarker(order)
        const id = asOverlayId(
          c.createOverlay({
            name: marker.name,
            points: [{ timestamp: ms, value: order.price }],
            lock: true,
            extendData: marker.extendData,
            onDrawEnd: () => true,
            onClick: () => {
              emitOrderClick(order)
              return true
            },
          }),
        )
        if (id) overlayIdsRef.current.set(orderId, id)
      }

      // Entry→exit connector for closed orders — only once BOTH endpoints'
      // bars are loaded (an entry before the loaded window would bend the
      // line to the edge bar; it materializes when pagination reaches it).
      if (!connectorIdsRef.current.has(orderId)) {
        const conn = backtestOrderConnector(order)
        if (
          conn &&
          conn.points.every((p) => p.timestamp >= firstMs && p.timestamp <= lastMs)
        ) {
          const connId = asOverlayId(
            c.createOverlay({
              name: 'segment',
              points: conn.points,
              lock: true,
              styles: {
                line: { color: conn.color, size: 1, style: LineType.Dashed, dashedValue: [2, 3, 8, 3] },
              },
              onDrawEnd: () => true,
              onClick: () => {
                emitOrderClick(order)
                return true
              },
            }),
          )
          if (connId) connectorIdsRef.current.set(orderId, connId)
        }
      }
    })
  }, [emitOrderClick])

  const pruneIfOverCap = useCallback((visFrom: number, visTo: number, pad: number) => {
    if (overlayIdsRef.current.size + connectorIdsRef.current.size <= MAX_OVERLAYS) return
    const c = chartMirrorRef.current
    const keepStart = Math.floor((visFrom - pad) / DAY) * DAY
    const keepEnd = Math.ceil((visTo + pad) / DAY) * DAY
    const removeFor = (ids: Map<string | number, string>, orderId: string | number) => {
      const id = ids.get(orderId)
      if (id) {
        try {
          c?.removeOverlay({ id })
        } catch {
          // Chart disposed mid-prune — ids are dropped below either way.
        }
      }
      ids.delete(orderId)
    }
    ordersRef.current.forEach((order, orderId) => {
      if (order.time >= keepStart && order.time <= keepEnd) return
      removeFor(overlayIdsRef.current, orderId)
      removeFor(connectorIdsRef.current, orderId)
      ordersRef.current.delete(orderId)
    })
    coveredRef.current = { start: keepStart, end: keepEnd }
  }, [])

  // Debounce funnel for range-change events + the post-fetch trailing check.
  // Ref-routed so scheduleCheck and checkCoverage don't form a useCallback cycle.
  const checkCoverageRef = useRef<() => void>(() => {})
  const scheduleCheck = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null
      checkCoverageRef.current()
    }, DEBOUNCE_MS)
  }, [])

  const checkCoverage = useCallback(async () => {
    const c = chartMirrorRef.current
    const bt = latestRef.current
    if (!activeRef.current || !c || !bt) return
    const list = c.getDataList()
    if (list.length === 0) return // candles not loaded yet; next range event retries
    const vr = c.getVisibleRange()
    const clamp = (i: number) => Math.min(Math.max(i, 0), list.length - 1)
    // klinecharts timestamps are ms; the events API takes unix seconds.
    const visFrom = Math.floor(list[clamp(vr.from)].timestamp / 1000)
    const visTo = Math.floor(list[clamp(vr.to - 1)].timestamp / 1000)

    // Pad, quantize to UTC days (stable fetch keys → LRU cache hits), and
    // clamp to the backtest's own range — nothing exists outside it.
    const pad = Math.max(DAY, Math.ceil((visTo - visFrom) * 0.25))
    let wantStart = Math.floor((visFrom - pad) / DAY) * DAY
    let wantEnd = Math.ceil((visTo + pad) / DAY) * DAY
    if (bt.startTs !== null) wantStart = Math.max(wantStart, Math.floor(bt.startTs / DAY) * DAY)
    if (bt.endTs !== null) wantEnd = Math.min(wantEnd, Math.ceil(bt.endTs / DAY) * DAY)
    if (wantStart >= wantEnd) {
      reconcileOverlays()
      return
    }

    const cov = coveredRef.current
    const disjoint = cov !== null && (wantEnd < cov.start || wantStart > cov.end)
    const deltas: Array<{ start: number; end: number }> = []
    if (cov === null || disjoint) {
      deltas.push({ start: wantStart, end: wantEnd })
    } else {
      if (wantStart < cov.start) deltas.push({ start: wantStart, end: cov.start })
      if (wantEnd > cov.end) deltas.push({ start: cov.end, end: wantEnd })
    }
    if (deltas.length === 0) {
      // Fully covered — just materialize any orders newly inside loaded bars.
      reconcileOverlays()
      return
    }
    if (inFlightRef.current) return // completion schedules a trailing re-check

    inFlightRef.current = true
    const epoch = epochRef.current
    try {
      const results = await Promise.all(
        deltas.map((d) => fetchBacktestEvents(bt.btid, d.start, d.end)),
      )
      if (epochRef.current !== epoch) return // cleared/superseded mid-flight
      if (disjoint) {
        // Viewport teleported away from the covered range — rebuild from the
        // new window only instead of accreting an unbounded marker set.
        for (const ids of [overlayIdsRef.current, connectorIdsRef.current]) {
          ids.forEach((id) => {
            try {
              chartMirrorRef.current?.removeOverlay({ id })
            } catch {
              // Chart disposed — stale ids are dropped below.
            }
          })
          ids.clear()
        }
        ordersRef.current.clear()
      }
      for (const orders of results) {
        for (const o of orders) {
          if (!ordersRef.current.has(o.orderId)) ordersRef.current.set(o.orderId, o)
        }
      }
      coveredRef.current =
        cov !== null && !disjoint
          ? { start: Math.min(cov.start, wantStart), end: Math.max(cov.end, wantEnd) }
          : { start: wantStart, end: wantEnd }
      reconcileOverlays()
      pruneIfOverCap(visFrom, visTo, pad)
    } finally {
      if (epochRef.current === epoch) {
        inFlightRef.current = false
        scheduleCheck() // the user may have out-panned this fetch
      }
    }
  }, [reconcileOverlays, pruneIfOverCap, scheduleCheck])
  useLayoutEffect(() => {
    checkCoverageRef.current = () => {
      void checkCoverage()
    }
  }, [checkCoverage])

  // ---- Reconcile lifecycle: subscribe while active, clear everything after ----
  const btid = latest?.btid ?? null
  useEffect(() => {
    if (!chart || !active) return
    void checkCoverage()
    const onRangeChange = () => scheduleCheck()
    chart.subscribeAction(ActionType.OnVisibleRangeChange, onRangeChange)
    return () => {
      try {
        chart.unsubscribeAction(ActionType.OnVisibleRangeChange, onRangeChange)
      } catch {
        // Chart already disposed.
      }
      if (debounceRef.current) {
        clearTimeout(debounceRef.current)
        debounceRef.current = null
      }
      clearAll()
    }
  }, [chart, active, btid, checkCoverage, scheduleCheck, clearAll])

  return { available, unavailableReason }
}
